import { Box, Text } from '@chakra-ui/layout'
import { Button, Input, useToast } from '@chakra-ui/react'
import { Layout } from 'components/atoms'
import { Icon } from 'components/atoms/Icon'
import { ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { useContractContext } from 'contexts/contract'
import { FormActions } from 'pages/Contracts/Components/StepsForm/styles'

import React, { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ViewFormFileDataProps } from '../../interfaces'

interface SendingFilesProps {
    onClose: () => void
    onSubmit?: () => void
}

const SendingFiles: React.FC<SendingFilesProps> = ({ onSubmit, onClose }) => {
    const { id = '' } = useParams()

    const toast = useToast()
    const toastSucess = useToast({ variant: 'solid-info' })
    const [loadingRequest, setLoadingRequest] = useState<boolean>(false)

    const { fileData, typeContract, setFileData, postAnnex, getContract } =
        useContractContext()

    const ref = useRef<HTMLInputElement>(null)

    const sendComputerFile = () => {
        ref.current?.click()
    }

    const deleteModalRef = useRef<ModalBuildHandle>(null)

    const handleOpenDeleteModal = (): void => {
        deleteModalRef.current?.onOpen()
    }

    const handleCloseDeleteModal = (): void => {
        deleteModalRef.current?.onClose()
    }

    const handleRemoveFile = () => {
        setFileData({} as ViewFormFileDataProps)
        handleCloseDeleteModal()
    }

    const handleSubmit = (id: string, data: ViewFormFileDataProps) => {
        setLoadingRequest(true)
        onSubmit && onSubmit()
        postAnnex(id, data.anexo)
            .then(() => {
                toastSucess({
                    title: 'Upload de anexo com sucesso!',
                    description: 'Acesse o seu anexo na área do contrato.',
                    position: 'bottom-left',
                    status: 'info',
                    isClosable: true
                })

                getContract(id)
                onClose()
            })
            .catch(() => {
                toast({
                    title: 'Sentimos muito!',
                    description: 'Tipo de arquivo não permitido',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
            .finally(() => {
                setFileData({} as ViewFormFileDataProps)
                setLoadingRequest(false)
            })
    }

    const typeOfOperation = useMemo(() => {
        switch (typeContract) {
            case 'CREATE':
                return 'REGISTRO_CONTRATO'

            case 'UPDATE':
                return 'ALTERACAO_REGISTRO_CONTRATO'

            case 'ADDITIVE':
                return 'REGISTRO_ADITIVO_CONTRATO'

            case 'EDITADDITIVE':
                return 'ALTERACAO_REGISTRO_ADITIVO_CONTRATO'

            default:
                return ''
        }
    }, [typeContract])

    const convertToBase64 = (data: File) => {
        const reader = new FileReader()

        reader.readAsDataURL(data)

        reader.onload = () => {
            setFileData({
                anexo: {
                    base64:
                        reader.result &&
                        reader.result.toString().split(',', 2)[1],
                    nome: data.name,
                    operacao: typeOfOperation
                }
            })
        }
    }

    const formatIsValid = (file: File | null | undefined): boolean => {
        if (!file) return false

        const fileName = file.name
        const acceptedFormats = ['jpg', 'pdf']

        const fileExt =
            fileName && fileName.substring(fileName.lastIndexOf('.') + 1)

        // Adicionado toLowerCase() para garantir a validação (ex: .JPG ou .PDF)
        return !!(fileExt && acceptedFormats.includes(fileExt.toLowerCase()))
    }    

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fileObj = event.target.files && event.target.files[0]
        
        event.target.value = ''

        if (!fileObj) {
            return 
        }

        if (!formatIsValid(fileObj)) {
            toast({
                title: 'Sentimos muito!',
                description: 'Tipo de arquivo não permitido',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return
        }

        const sizeInKB = fileObj.size / 1024
        const maxSizeInKB = 5120 

        if (sizeInKB > maxSizeInKB) {
            toast({
                title: 'Sentimos muito!',
                description:
                    'O arquivo deve possuir tamanho máximo de 5 Mb (cinco megabytes), nos formatos JPG ou PDF.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return 
        }

        convertToBase64(fileObj)
    }

    return (
        <Layout type="container" px={8} display="grid" gap={6} padding="0">
            <Box
                display="flex"
                textAlign="center"
                alignItems="center"
                justifyContent="center"
                flexDirection="row"
                zIndex={12}
                pt="25px"
            >
                <Text
                    fontSize="xl"
                    whiteSpace="pre"
                    color="black"
                    fontWeight="700"
                >
                    Anexos
                </Text>
            </Box>
            <Box
                textAlign="center"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap={2}
                height="300px"
                position="relative"
            >
                {!fileData?.anexo ? (
                    <Box
                        data-testid="boxSendComputerFile"
                        textAlign="center"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        gap={2}
                        zIndex={12}
                        onClick={sendComputerFile}
                        cursor="pointer"
                    >
                        <Text whiteSpace="pre" color="black" fontWeight="700">
                            Nenhum anexo selecionado.
                        </Text>
                        <Text
                            whiteSpace="pre"
                            color="black"
                            fontWeight="700"
                            textDecoration="underline"
                            ref={ref}
                        >
                            Faça upload agora mesmo.
                        </Text>
                        <Input
                            data-testid="input-file"
                            ref={ref}
                            type="file"
                            display={'none'}
                            accept=".jpg,.pdf"
                            onChange={handleFileChange}
                        />
                        <Text
                            whiteSpace="pre"
                            color="black"
                            maxWidth="292px"
                            fontSize={12}
                        >
                            O arquivo deve possuir tamanho máximo de 5 Mb <br />{' '}
                            (cinco megabytes), nos formatos JPG ou PDF.
                        </Text>
                        <Box position="absolute" zIndex="-1" cursor="pointer">
                            <Icon name="file-alt-white" size={124} />
                        </Box>
                    </Box>
                ) : (
                    <Box
                        bg="grayscale.50"
                        width="100%"
                        height="52px"
                        position="absolute"
                        display="flex"
                        alignItems="center"
                        flexDirection="row"
                        justifyContent="space-between"
                        padding="0 30px"
                        top="0px"
                    >
                        <Text
                            whiteSpace="pre"
                            color="grayscale.800"
                            fontSize={14}
                        >
                            {fileData?.anexo.nome}
                        </Text>
                        <Box display="flex" flexDir="row">
                            <Icon
                                data-testid="removeFile"
                                name="delet"
                                onClick={handleOpenDeleteModal}
                                cursor="pointer"
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <Layout
                type="row"
                gutter={{ y: 1.5 }}
                data-testid="form-fields"
            ></Layout>
            <FormActions>
                <Button
                    data-testid="form-action-cancel"
                    colorScheme="primary"
                    variant="outline"
                    onClick={onClose}
                >
                    Cancelar
                </Button>
                <Button
                    data-testid="form-action-confirm"
                    colorScheme="primary"
                    isDisabled={!fileData?.anexo}
                    isLoading={loadingRequest}
                    onClick={() =>
                        handleSubmit(id, fileData as ViewFormFileDataProps)
                    }
                >
                    Finalizar
                </Button>
            </FormActions>
            <ModalBuild
                ref={deleteModalRef}
                closeOnOverlayClick={false}
                title="Confirmação de exclusão"
                size="md"
                actions={{
                    position: 'flex-end',
                    childrens: [
                        {
                            text: 'Cancelar',
                            handle: 'cancel',
                            variant: 'outline'
                        },
                        {
                            text: 'Deletar',
                            handle: 'confirm',
                            colorScheme: 'red'
                        }
                    ]
                }}
                onConfirm={handleRemoveFile}
                onCancel={handleCloseDeleteModal}
            >
                <Text px={6} py={4}>
                    Você realmente deseja deletar? Uma vez deletado, não será
                    possível voltar atrás.
                </Text>
            </ModalBuild>
        </Layout>
    )
}

export default SendingFiles
